import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { MissionCoverImageField } from "@/components/missions/mission-cover-image-field"
import { FormField } from "@/components/missions/mission-form"
import { MissionProductPicker } from "@/components/missions/mission-product-picker"
import { MissionProductSummaryCard } from "@/components/missions/mission-product-summary-card"
import {
  MissionProductDetachedPin,
  MissionProductVariantPin,
} from "@/components/missions/mission-product-variant-pin"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { formatGiftValue } from "@/lib/format-gift-value"
import { missionProductParamNames } from "@/lib/mission-product-param-names"
import type { Locale } from "@/types"
import {
  type AttachedCatalogProduct,
  type CatalogPickerPayload,
  type CatalogPickerProduct,
  type MissionProductFormData,
  missionProductPinState,
  pickFirstErrorMessage,
} from "@/types/missions"

/**
 * OKS-495 — Gift-product authoring fieldset, revealed on the shared mission
 * form (`mission-form.tsx`) only for gifting mission types (Drop +
 * Promotional). Activates the dormant OKS-482 creator gifting flow by giving
 * brands the entry point to author a `MissionProduct`.
 *
 * # Wire shape (Crew form rules)
 *
 * Plain shadcn `Input`/`Label` (via the form's shared `FormField`) inside the
 * parent Inertia `<Form>`; every input is `name="mission[mission_product_
 * attributes][...]"` so Rails' `accepts_nested_attributes_for` upserts the
 * 1:1 product in the same save. NO shadcn Form / RHF / zod. Server validation
 * is the floor — errors surface from the render-prop `errors` object under
 * the dotted nested keys (`errors[`mission_products[${index}].name`]`, etc.). The
 * `value` input binds the major-unit virtual (`MissionProduct#value`), so its
 * validation error lands on `mission_product.value_cents`.
 *
 * # Photo
 *
 * Reuses `MissionCoverImageField` (plain multipart, default Active Storage
 * service — NOT direct upload) with product-specific labels + the nested
 * `photo` / `remove_photo` param names. The hidden `id` input lets Rails
 * target the existing product row on edit; an empty fieldset is rejected
 * server-side (`reject_if`) so a gifting mission can be drafted without a
 * product.
 *
 * # Reset on type switch
 *
 * The parent re-mounts this component (via a `key` bump) on a confirmed
 * switch away from a gifting type, clearing the uncontrolled inputs — mirrors
 * the promotional-fields reset.
 */
export interface MissionProductFieldsetProps {
  /** Existing product (Edit), or null on New / no product yet. */
  product: MissionProductFormData | null
  /** Inertia `<Form>` render-prop errors (dotted nested keys). */
  errors: Record<string, unknown>
  /** Called on first edit so the parent can prompt before a type switch. */
  onDirty: () => void
  /**
   * OKS-933 — deferred picker payload. `undefined` while Inertia resolves it.
   *
   * ⚠️ REQUIRED, not optional, and that is the fix for how this shipped broken.
   * It was `catalog?:` — so `mission-form.tsx` never passing it TYPE-CHECKED,
   * `PickerBody` took its unresolved branch forever, and the picker was
   * permanently empty. The server was correct and the component was correct;
   * only the parent wiring was absent, and nothing could see it. A required
   * prop makes the compiler enumerate every call site.
   */
  catalog: CatalogPickerPayload | undefined
  /**
   * OKS-933 (D2) — false once the mission has collabs. Gates Detach ONLY.
   *
   * ⚠️ REQUIRED for the same reason, plus one worse: it defaulted to `true`,
   * so an unwired call site failed OPEN — rendering Detach on a mission that
   * must not offer it. A guard whose default is the permissive value is the
   * one you least want optional.
   */
  canDetach: boolean

  /**
   * OKS-990 — this row's position in the posted set. Defaults to 0, which is
   * exactly today's single-product form; T18 renders further indices.
   */
  index?: number
}

/**
 * OKS-990 — the offer is a SET, so the posted name carries a row index and the
 * server permits `mission_products_attributes`.
 *
 * ⚠️ `[0]` is not a placeholder for "the product". Rails parses
 * `mission[mission_products_attributes][0][name]` into an INDEX-KEYED HASH
 * (`{"0" => {...}}`), which is the encoding the F11 blob guard has to handle —
 * it passes `respond_to?(:key?)` and dies at `key?(:photo)`, one clause later
 * than an Array would. T18 appends further indices; nothing about the wire
 * changes when it does.
 */
const nameFor = (index: number) =>
  `mission[mission_products_attributes][${index}]`

/**
 * OKS-1000 — the value hint's DOM id, shared by the `<p>` that renders it and
 * the `aria-describedby` that points at it.
 *
 * ⚠️ Derived from the input's own id so the pair cannot drift apart in a
 * rename. It is deliberately NOT index-keyed, because
 * `id="mission-product-value"` is not either — see NOTES N1: the seven
 * hardcoded fieldset ids collide the day OKS-990 T18 renders a second row, and
 * this id inherits that limitation rather than adding a new one. Fixing it here
 * alone would leave a hint correctly associated inside a fieldset whose other
 * six fields are not.
 */
const VALUE_HINT_ID = "mission-product-value-hint"

/**
 * OKS-933 — the fieldset has THREE states and never two editors (UX §2).
 *
 *   S1  no product yet          → "Choose from catalog" + "Enter manually"
 *   S2  `product_id != null`    → summary card + the same inputs, still editable
 *   S3  `product_id IS NULL`    → exactly today's rendering + "Link to a catalog
 *       with fields populated     product"; S3b adds the detached-pin alert
 *
 * ⚠️ The branch is on the DATA TUPLE, not on page position — see
 * `missionProductPinState`. Rows 2 and 5 of UX §5 differ only by `product_id`.
 */
export function MissionProductFieldset({
  product,
  index = 0,
  errors,
  onDirty,
  catalog,
  canDetach,
}: MissionProductFieldsetProps) {
  const { t, i18n } = useTranslation()

  // Picker selection is LOCAL until the mission form is submitted (UX §3) —
  // nothing is persisted by opening or choosing. `picked` drives the optimistic
  // S2 render; `product.product_id` drives it after a save.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [picked, setPicked] = useState<CatalogPickerProduct | null>(null)
  const [manual, setManual] = useState(false)
  // OKS-1000 (D8a/D8b) — the hint needs to know whether the two money inputs
  // are EMPTY, and the prefill needs to write into them. Both inputs stay
  // UNCONTROLLED (`defaultValue` + a ref); this state mirrors their emptiness
  // for rendering only, and never drives their value.
  //
  // ⚠️ A controlled binding was the obvious alternative and is wrong here: the
  // brand must be able to type over a prefilled number freely, and the Crew
  // form rules put the server's validation, not React, in charge of the value.
  const valueRef = useRef<HTMLInputElement>(null)
  const currencyRef = useRef<HTMLInputElement>(null)
  const [valueEmpty, setValueEmpty] = useState(!product?.value)
  const [currencyEmpty, setCurrencyEmpty] = useState(!product?.currency)
  // Selection awaiting the S3 overwrite confirm. Never applied until confirmed.
  const [pendingLink, setPendingLink] = useState<CatalogPickerProduct | null>(
    null,
  )

  const attached: AttachedCatalogProduct | null = picked
    ? {
        id: picked.id,
        title: picked.title,
        image_url: picked.image_url,
        archived: false,
        manual: picked.manual,
        min_price_cents: picked.min_price_cents,
        max_price_cents: picked.max_price_cents,
        currency: picked.currency,
        // The picker never lists archived variants (AC2), so the optimistic
        // S2 card carries exactly what the operator just chose from.
        variants: picked.variants,
      }
    : (product?.product ?? null)

  const pinState = missionProductPinState(product)
  // S1 is "no row at all"; a row with populated fields and no product is S3.
  const isS1 = product == null && !picked && !manual

  /**
   * OKS-933 (OQ-5 / UX §2 S3) — linking a LEGACY row overwrites four snapshot
   * columns the operator may have typed by hand, so it goes behind a confirm
   * that NAMES what will be replaced. Cancel is the default (UX §5:45).
   *
   * ⚠️ Gated on `linkWouldOverwrite`, not shown unconditionally. From S1 there
   * is nothing to overwrite and a confirm would be a dialog asking permission
   * to fill in blank fields — noise that trains the operator to dismiss it,
   * which is exactly what makes it useless on the one screen that needs it.
   *
   * ⚠️ This was MISSING until CI's unused-key guard flagged the copy. The keys
   * were nearly deleted as "orphans" of D2's Change-product removal — they
   * read like that confirm — which would have converted a missing dialog into
   * a removed requirement: green CI, invisible defect, and the only artifact
   * documenting it gone. "Unused" is a measurement; "dead" is a judgement, and
   * only the spec can make it.
   */
  // ⚠️ Truthiness on purpose, expressed explicitly rather than with `||`.
  // eslint pushes `||` → `??` here, and that swap would be a BUG: `??` only
  // falls through on null/undefined, so an EMPTY-string column would count as
  // "something to overwrite" and pop a confirm over blank fields.
  const linkWouldOverwrite =
    product != null &&
    product.product_id == null &&
    [product.name, product.value, product.currency, product.sku].some(
      (field) => field != null && field !== "",
    )

  // ⚠️ Picker selection wins while the Sheet session is live (optimistic S2);
  // otherwise the SERVER's attached-product variants, which INCLUDE archived
  // ones so a pinned-then-archived member keeps rendering.
  //
  // This read `picked?.variants ?? []`, so on any page LOAD of an attached
  // mission the collapsible expanded to zero checkboxes: UX §5 row 4
  // unrenderable, AC13 failing, and no way to change a pin without detaching
  // and re-picking.
  const pinVariants = picked?.variants ?? product?.product?.variants ?? []
  const archivedVariantIds = pinVariants
    .filter((variant) => variant.archived)
    .map((variant) => variant.id)

  function selectProduct(selected: CatalogPickerProduct) {
    setPicked(selected)
    prefillMoneyFrom(selected)
    onDirty()
  }

  /**
   * OKS-1000 (D8b) — write the catalogue price into the two money inputs.
   *
   * ⚠️ Fires ONLY from a fresh pick, and only into an EMPTY input. Never on
   * mount, and never over something already typed.
   *
   * A prefill on page load would silently refill a value the brand
   * deliberately CLEARED — which is the row shape this whole ticket exists to
   * serve, and the one the catalogue fallback is for. On load a blank field
   * gets the hint instead, which states what will happen without changing what
   * the form will post.
   *
   * ⚠️ Writing `.value` imperatively is what keeps the inputs uncontrolled.
   * Setting `defaultValue` on a mounted input is a silent no-op in React —
   * it would look correct in review and do nothing at runtime.
   */
  function prefillMoneyFrom(selected: CatalogPickerProduct) {
    if (valueEmpty && valueRef.current && selected.min_price_cents != null) {
      // Major units with a `.` separator — the input is `type="number"`, whose
      // value is locale-independent regardless of how the browser displays it.
      valueRef.current.value = (selected.min_price_cents / 100).toFixed(2)
      setValueEmpty(false)
    }
    if (currencyEmpty && currencyRef.current && selected.currency) {
      currencyRef.current.value = selected.currency
      setCurrencyEmpty(false)
    }
  }

  const valueError = pickFirstErrorMessage(
    errors[`mission_products[${index}].value_cents`] as never,
  )

  /**
   * OKS-1000 (D8a) — the nudge, in three mutually exclusive variants.
   *
   * ⚠️ Shipping only the first would be a LIE. « elle sera déduite du
   * catalogue » is true only when a FALLBACK actually exists; on a
   * hand-entered product, or an attached one priced nowhere, nothing is coming
   * and the honest hint says so.
   *
   * ⚠️ The variant keys on whether a FALLBACK exists (rung 2 or 3), not on
   * whether a catalogue MINIMUM exists. A pinned-variant row has no
   * `min_price_cents` story to tell and still has a value coming — keying on
   * the catalogue price sent exactly those rows down the `blank` branch,
   * promising nothing while a value was in fact coming.
   *
   * ⚠️ The third fires on a NON-empty value and is the one BRIEF §8.0.1 L4
   * bought: value entered, currency blank. `MissionProduct#effective_value`
   * needs both halves, so that row resolves `none` and creators see nothing —
   * the reported problem, reached from the opposite end, by a brand who
   * entered exactly what the ticket asks for.
   *
   * ⚠️ Suppressed under a validation error on the same field: an error and a
   * nudge stacked under one input is noise, and the error is the more urgent
   * of the two.
   *
   * # ⚠️ Where the promised number comes from, and where it must NOT
   *
   * The hint names what a creator will see if the field stays blank, so its
   * source is the SERVER's fallback resolution (`MissionProduct#fallback_value`
   * — the ladder below rung 1), threaded in on the form payload.
   *
   * Its first version read `attached.min_price_cents` directly and was
   * therefore blind to the variant pin and to the range flag. PO's walk
   * measured it correct on ONE catalogue mission in four: on a mission whose
   * gift is a pinned 129,00 € variant it promised the product's 49,00 €
   * minimum — an 80 € gap, in the direction that makes the drop look less
   * valuable than it is.
   *
   * ⚠️ The obvious repair — re-derive the pin here — is the wrong one. It puts
   * a second copy of the resolution in the frontend and breaks D1's "one
   * resolution, one place"; it would also be the copy that drifts, since it is
   * the one no rung-boundary spec covers.
   *
   * ⚠️ `fallback_value`, NOT `effective_value`. The two coincide on every row
   * whose own `value_cents` is nil and diverge on the row where this hint
   * matters most: one with a SAVED value whose operator has cleared the input
   * and not yet saved. The input is empty so the hint renders, but the column
   * is not, so `effective_value` would report the amount the operator is in the
   * act of deleting.
   *
   * # The session pick is the one case the server cannot answer
   *
   * A product picked THIS session has no saved row and no pin yet, so
   * `picked.min_price_cents` genuinely is what a creator would get, and the
   * server has nothing to say about it. Hence: the server's fallback for an
   * attached row, the pick for a session pick.
   */
  const locale = (i18n.language === "fr" ? "fr" : "en") as Locale
  const fallbackPrice = picked
    ? formatGiftValue(picked.min_price_cents, picked.currency, locale)
    : formatGiftValue(
        product?.fallback_value_cents,
        product?.fallback_currency,
        locale,
      )
  // A floor is stated as a floor. Promising « 49,00 € » for a gift that starts
  // at 49,00 € is the same false precision the creator surfaces avoid.
  const fallbackIsRange = picked
    ? picked.max_price_cents != null &&
      picked.max_price_cents !== picked.min_price_cents
    : (product?.fallback_is_range ?? false)
  const catalogPrice =
    fallbackPrice && fallbackIsRange
      ? t("collabs.detail_offer.value_from", { value: fallbackPrice })
      : fallbackPrice
  const valueHint = valueError
    ? null
    : !valueEmpty && currencyEmpty
      ? {
          variant: "currency_missing",
          text: t("missions.form.product.value.currency_missing_hint"),
        }
      : !valueEmpty
        ? null
        : catalogPrice
          ? {
              variant: "catalog",
              text: t("missions.form.product.value.catalog_hint", {
                price: catalogPrice,
              }),
            }
          : {
              variant: "blank",
              text: t("missions.form.product.value.blank_hint"),
            }

  return (
    <fieldset
      className="flex flex-col gap-4 rounded-md border border-dashed p-4"
      data-testid="mission-product-fields"
    >
      <legend className="px-1 text-sm font-medium">
        {t("missions.form.product.group_label")}
      </legend>
      <p className="text-muted-foreground text-xs">
        {t("missions.form.product.group_helper")}
      </p>

      {/* ---- OKS-933 — catalog block, above the inputs (UX §2) ---- */}

      <MissionProductPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        catalog={catalog}
        onSelect={(selected) => {
          // S3 → confirm first; S1 has nothing to overwrite, so apply directly.
          if (linkWouldOverwrite) setPendingLink(selected)
          else selectProduct(selected)
        }}
      />

      {/* OQ-5 / UX §2 S3 — names the four columns that will be replaced and
          the three that are kept, rather than a generic "are you sure?". */}
      <AlertDialog
        open={pendingLink != null}
        onOpenChange={(open) => {
          if (!open) setPendingLink(null)
        }}
      >
        <AlertDialogContent data-testid="mission-product-link-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("missions.form.product.link_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("missions.form.product.link_confirm_body", {
                title: pendingLink?.title ?? "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {/* ⚠️ Cancel is the DEFAULT (UX §2 S3). The destructive path must
                not be the one a stray Enter takes. */}
            <AlertDialogCancel data-testid="mission-product-link-cancel">
              {t("missions.form.product.link_confirm_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="mission-product-link-confirm-action"
              // eslint-disable-next-line no-restricted-syntax -- closes on click by design (OKS-1023)
              onClick={() => {
                if (pendingLink) selectProduct(pendingLink)
                setPendingLink(null)
              }}
            >
              {t("missions.form.product.link_confirm_cta")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ⚠️ TOP-LEVEL param, deliberately NOT nested under
          `mission_products_attributes`. `product_id` is never permitted into the
          nested mass-assignment path, which is what makes `X → Y`
          unconstructible there (D2). This goes through
          `MissionProducts::Attacher`, which carries the guard. */}
      {picked && (
        <input
          type="hidden"
          name={missionProductParamNames(index).attachProductId}
          value={picked.id}
          data-testid="mission-product-attach-id"
        />
      )}

      {attached ? (
        <MissionProductSummaryCard
          product={attached}
          canDetach={canDetach}
          index={index}
        />
      ) : isS1 ? (
        <div className="flex flex-col items-start gap-2">
          <Button
            type="button"
            size="sm"
            onClick={() => setPickerOpen(true)}
            data-testid="catalog-picker-open"
          >
            {t("missions.form.product.choose_from_catalog")}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto px-0 text-xs"
            onClick={() => setManual(true)}
            data-testid="mission-product-enter-manually"
          >
            {t("missions.form.product.enter_manually")}
          </Button>
        </div>
      ) : (
        /* S3 — exactly today's rendering plus ONE muted action. This is the
           whole answer to "two ways to express the same thing": one surface,
           three states, never a catalog editor beside a text editor. */
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-auto w-fit px-0 text-xs"
          onClick={() => setPickerOpen(true)}
          data-testid="catalog-picker-open"
        >
          {t("missions.form.product.link_to_catalog")}
        </Button>
      )}

      {/* S3b (D3) — product gone, restriction still recorded. Renders HERE, in
          S3, because with `product_id` NULL there is no card to hang it under. */}
      {pinState === "detached-orphan" && (
        <MissionProductDetachedPin index={index} />
      )}

      {attached && (
        <>
          <p className="text-muted-foreground text-xs">
            {t("missions.form.product.copied_helper")}
          </p>
          {product && (
            <MissionProductVariantPin
              product={product}
              variants={pinVariants}
              archivedVariantIds={archivedVariantIds}
              index={index}
            />
          )}
        </>
      )}

      {/* Targets the existing product row on edit so the nested upsert
          updates rather than duplicates. */}
      {product ? (
        <input
          type="hidden"
          name={`${nameFor(index)}[id]`}
          value={product.id}
          data-testid="mission-product-id"
        />
      ) : null}

      {/* ⚠️ The inputs below stay MOUNTED in every state, including S1.
          Unmounting them would discard anything already typed the moment the
          operator toggles a state, and it would break the shipped `reject_if`
          contract that an entirely-empty fieldset is a valid "no product yet"
          save. S1 hides them behind the two actions above rather than removing
          them. */}

      {/* NOT HTML5-`required`: an entirely-empty fieldset is a valid
          "no product yet" save (server `reject_if`). The "*" in the label
          signals it's required WHEN authoring; the server enforces presence
          once any other product field is filled. A hard `required` here
          would block every gifting-mission save that omits a product. */}
      <FormField
        id="mission-product-name"
        name={`${nameFor(index)}[name]`}
        label={t("missions.form.product.name.label")}
        placeholder={t("missions.form.product.name.placeholder")}
        defaultValue={product?.name ?? ""}
        error={pickFirstErrorMessage(
          errors[`mission_products[${index}].name`] as never,
        )}
        onChange={onDirty}
      />

      <MissionCoverImageField
        testId="mission-product-photo-field"
        inputName={`${nameFor(index)}[photo]`}
        removeName={`${nameFor(index)}[remove_photo]`}
        currentUrl={product?.photo_url ?? null}
        hasCoverImage={product?.has_photo ?? false}
        isLegacy={false}
        // OKS-643 — product photos use the original blob (no `:card` variant
        // / card-sharpness target), so the cover-specific below-minimum
        // advisory doesn't apply here. Disable the dimension probe.
        minDimensions={null}
        error={
          pickFirstErrorMessage(
            errors[`mission_products[${index}].photo`] as never,
          ) ?? undefined
        }
        labels={{
          label: "missions.form.product.photo_label",
          hint: "missions.form.product.photo_hint",
          previewAlt: "missions.form.product.photo_preview_alt",
          upload: "missions.form.product.photo_upload",
          replace: "missions.form.product.photo_replace",
          remove: "missions.form.product.photo_remove",
          removeConfirmTitle:
            "missions.form.product.photo_remove_confirm_title",
          removeConfirmBody: "missions.form.product.photo_remove_confirm_body",
          removeConfirmCancel:
            "missions.form.product.photo_remove_confirm_cancel",
          removeConfirmCta: "missions.form.product.photo_remove_confirm_cta",
        }}
      />

      {/* Quantity / Value stack to two rows on mobile, sit 2-up ≥ sm. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <FormField
          id="mission-product-quantity"
          name={`${nameFor(index)}[quantity]`}
          label={t("missions.form.product.quantity.label")}
          helper={t("missions.form.product.quantity.helper")}
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          defaultValue={String(product?.quantity ?? 1)}
          error={pickFirstErrorMessage(
            errors[`mission_products[${index}].quantity`] as never,
          )}
          onChange={onDirty}
        />

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[2fr_1fr] gap-2">
            <FormField
              id="mission-product-value"
              name={`${nameFor(index)}[value]`}
              label={t("missions.form.product.value.label")}
              placeholder={t("missions.form.product.value.placeholder")}
              type="number"
              inputMode="numeric"
              min={0}
              step={0.01}
              defaultValue={product?.value ?? ""}
              inputRef={valueRef}
              error={valueError}
              onChange={onDirty}
              onValueChange={(next) => setValueEmpty(next.trim() === "")}
              // ⚠️ Only when the hint actually renders. A constant id here
              // would leave `aria-describedby` pointing at a `<p>` that is not
              // in the DOM on most rows — a dangling reference, which assistive
              // tech treats as no description at all while a presence-only
              // assertion still passes.
              describedBy={valueHint ? VALUE_HINT_ID : undefined}
            />
            <FormField
              id="mission-product-currency"
              name={`${nameFor(index)}[currency]`}
              label={t("missions.form.product.currency.label")}
              placeholder={t("missions.form.product.currency.placeholder")}
              defaultValue={product?.currency ?? ""}
              inputRef={currencyRef}
              error={pickFirstErrorMessage(
                errors[`mission_products[${index}].currency`] as never,
              )}
              onChange={onDirty}
              onValueChange={(next) => setCurrencyEmpty(next.trim() === "")}
            />
          </div>
          {valueHint && (
            <p
              // ⚠️ The id is what associates this text with the input above it.
              // Without it the hint is visible to sighted users and absent to a
              // screen reader — the field's shipped `gift_ttl` sibling one row
              // down has been correctly associated since OKS-935, so this was an
              // inconsistency within one fieldset rather than a missing pattern.
              id={VALUE_HINT_ID}
              className="text-muted-foreground text-xs"
              data-testid="mission-product-value-hint"
              data-hint-variant={valueHint.variant}
            >
              {valueHint.text}
            </p>
          )}
        </div>
      </div>

      <FormField
        id="mission-product-sku"
        name={`${nameFor(index)}[sku]`}
        label={t("missions.form.product.sku.label")}
        placeholder={t("missions.form.product.sku.placeholder")}
        defaultValue={product?.sku ?? ""}
        error={pickFirstErrorMessage(
          errors[`mission_products[${index}].sku`] as never,
        )}
        onChange={onDirty}
      />

      <FormField
        id="mission-product-stock"
        name={`${nameFor(index)}[stock]`}
        label={t("missions.form.product.stock.label")}
        helper={t("missions.form.product.stock.helper")}
        type="number"
        inputMode="numeric"
        min={0}
        step={1}
        defaultValue={product?.stock != null ? String(product.stock) : ""}
        error={pickFirstErrorMessage(
          errors[`mission_products[${index}].stock`] as never,
        )}
        onChange={onDirty}
      />

      {/*
        OKS-935 S3 — the per-mission TTL override (AC2).

        ⚠️ This value belongs to the gift THIS mission ships, and is read by a
        LATER mission's recency guard. It is never consulted for this mission's
        own assign preview (PRD D6). The helper line says so in the brand's
        words: blank inherits the variant's TTL, then the product's.

        No Form of its own — it is a `name`d input inside the parent mission
        `<Form>` and saves with the mission, so the error key is the nested
        `mission_product.gift_ttl_days`.
      */}
      <FormField
        id="mission-product-gift-ttl"
        name={`${nameFor(index)}[gift_ttl_days]`}
        label={t("missions.form.product.gift_ttl.label")}
        helper={t("missions.form.product.gift_ttl.hint")}
        placeholder={t("missions.form.product.gift_ttl.placeholder")}
        // UX §2 binds « jours » as a unit BESIDE the control. It was the only
        // part of the S3 field never wired, which is why the key showed up as
        // orphaned — the feature was incomplete, not the key dead.
        suffix={t("missions.form.product.gift_ttl.suffix")}
        type="number"
        inputMode="numeric"
        min={1}
        step={1}
        defaultValue={
          product?.gift_ttl_days != null ? String(product.gift_ttl_days) : ""
        }
        error={pickFirstErrorMessage(
          errors[`mission_products[${index}].gift_ttl_days`] as never,
        )}
        onChange={onDirty}
      />
    </fieldset>
  )
}

export default MissionProductFieldset
